/**
 * Daten eines Formulars nach seiner Vorlage prüfen und auf die bekannte Form bringen – unbekannte Felder fallen weg,
 * Texte werden gekürzt, Häkchen sind nur `true`, Daten nur gültige YYYY-MM-DD. Gleiche Regeln wie die bisherigen
 * Formulare (lib/checkliste.js), damit bestehende Dateien unverändert gelesen und geschrieben werden.
 *
 * Form der Daten:
 *   { version, titel, <kopf-feld>: wert, <tabelle>: [{ nr, <spalte>: wert }], <festeZeilen>: { <zeile>: { <spalte>: wert } },
 *     <fuss-feld>: wert }
 */

export const text = (v, max = 500) => String(v ?? '').replace(/[\u0000-\u001f\u007f]/g, ' ').trim().slice(0, max);
export const datum = (v) => (/^\d{4}-\d{2}-\d{2}$/.test(String(v ?? '')) && !Number.isNaN(Date.parse(v)) ? String(v) : '');
export const zeit = (v) => {
  const m = String(v ?? '').trim().match(/^(\d{1,2})[:.](\d{2})$/);
  return m && Number(m[1]) < 24 && Number(m[2]) < 60 ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
};
/** Zahl in einheitlicher Schreibweise («1.0» → «1», «2,50» → «2.5») – so wie Excel sie zurückgibt. */
const zahl = (v) => {
  const s = String(v ?? '').trim().replace(',', '.');
  return /^-?\d+(\.\d+)?$/.test(s) ? String(Number(s)) : '';
};
const istLeer = (w) => w === '' || w === false || (Array.isArray(w) && !w.length);

/** Leerer Wert eines Felds (so steht es in einem neuen Formular). */
export const leerWert = (f) => (f.typ === 'haken' ? false : f.typ === 'mehrfach' || f.typ === 'fotos' ? [] : '');

/** Unterschrift: PNG als data-URL, höchstens ~220 KB. */
const UNTERSCHRIFT = /^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/;
export const MAX_UNTERSCHRIFT = 300_000;

/** Ein Feldwert nach Typ. */
export function feldWert(f, v) {
  switch (f.typ) {
    case 'haken': return v === true;
    case 'datum': return datum(v);
    case 'zeit': return zeit(v);
    case 'zahl': return zahl(v);
    case 'auswahl': {
      const t = text(v, f.max ?? 120);
      return f.frei || !t || f.optionen.includes(t) ? t : '';
    }
    case 'mehrfach': {
      const liste = (Array.isArray(v) ? v : typeof v === 'string' ? v.split(/\s*,\s*/) : []).map((x) => text(x, f.max ?? 80)).filter(Boolean);
      return [...new Set(liste)].filter((x) => f.frei || (f.optionen ?? []).includes(x)).slice(0, 20);
    }
    case 'fotos': {
      // Verweise auf hochgeladene Dateien im Auftrag; noch nicht hochgeladene (nur auf dem Gerät) fallen weg
      const liste = Array.isArray(v) ? v : [];
      return liste.filter((x) => /^\d{1,20}$/.test(String(x?.dateiId ?? '')))
        .map((x) => ({ name: text(x.name, 120) || 'Foto.jpg', dateiId: String(x.dateiId) })).slice(0, 30);
    }
    case 'unterschrift': {
      const s = String(v ?? '');
      return s.length <= MAX_UNTERSCHRIFT && UNTERSCHRIFT.test(s) ? s : '';
    }
    default: return text(v, f.max ?? (f.typ === 'text' ? 2000 : 120));
  }
}

const zeileAus = (spalten, roh) => Object.fromEntries(spalten.map((f) => [f.id, feldWert(f, roh?.[f.id])]));

/** Leeres Formular (für «neu»). `zeilen`: Anzahl Zeilen je Tabelle, z. B. { zonen: 3 }. */
export function leeresFormular(v, { titel = '', zeilen = {} } = {}) {
  const d = { version: v.version, titel };
  for (const f of v.kopf ?? []) d[f.id] = leerWert(f);
  for (const a of v.abschnitte) {
    if (a.art === 'tabelle') d[a.id] = Array.from({ length: Math.min(zeilen[a.id] ?? 0, a.max) }, (_, i) => ({ nr: i + 1, ...zeileAus(a.spalten, {}) }));
    else d[a.id] = Object.fromEntries(a.zeilen.map((z) => [z.id, zeileAus(a.spalten, {})]));
  }
  for (const f of v.fuss ?? []) d[f.id] = leerWert(f);
  return d;
}

const kopie = (w) => (Array.isArray(w) ? [...w] : w);
/** Neue Tabellenzeile mit den Standardwerten der Spalten (`standard`). */
export const neueZeile = (a) => Object.fromEntries(a.spalten.map((f) => [f.id, f.standard !== undefined ? kopie(f.standard) : leerWert(f)]));

/**
 * Standardwerte eines neuen Formulars (nur beim Anlegen, nie beim Lesen einer Datei): `standard` an Kopf- und
 * Schlussfeldern, an Spalten fester Zeilen und pro Zeile (`zeilen[].standard`), z. B. «RCD geprüft: i. O.».
 */
export function standardsSetzen(v, d) {
  for (const f of [...(v.kopf ?? []), ...(v.fuss ?? [])]) if (f.standard !== undefined) d[f.id] = kopie(f.standard);
  for (const a of v.abschnitte.filter((x) => x.art === 'festeZeilen')) {
    for (const z of a.zeilen) {
      for (const f of a.spalten) if (f.standard !== undefined) d[a.id][z.id][f.id] = kopie(f.standard);
      Object.assign(d[a.id][z.id], z.standard ?? {});
    }
  }
  return d;
}

/** @param {object} v Vorlage · @param {unknown} daten Eingabe */
export function normalisieren(v, daten) {
  const d = daten && typeof daten === 'object' ? daten : {};
  const out = { version: v.version, titel: text(d.titel, v.titelFeld.max) };
  for (const f of v.kopf ?? []) out[f.id] = feldWert(f, d[f.id]);
  for (const a of v.abschnitte) {
    if (a.art === 'tabelle') {
      let zeilen = (Array.isArray(d[a.id]) ? d[a.id] : []).map((z) => zeileAus(a.spalten, z));
      // `leereWeg`: ganz leere Zeilen fallen weg (z. B. angelegte, nie ausgefüllte Messungen) – wie beim Lesen der Datei
      if (a.leereWeg) zeilen = zeilen.filter((z) => !Object.values(z).every(istLeer));
      out[a.id] = zeilen.slice(0, a.max).map((z, i) => ({ nr: i + 1, ...z }));
    } else {
      out[a.id] = Object.fromEntries(a.zeilen.map((z) => [z.id, zeileAus(a.spalten, d[a.id]?.[z.id])]));
    }
  }
  for (const f of v.fuss ?? []) out[f.id] = feldWert(f, d[f.id]);
  return out;
}
